import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, LineChart, Line,
} from "recharts";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";
import ExportCsvButton from "../../components/ExportCsvButton";

const TEAL = "rgb(var(--mb-primary))";
const isoDate = (d) => d.toISOString().slice(0, 10);
const today = new Date(); today.setHours(0,0,0,0);
const start30 = new Date(today); start30.setDate(today.getDate() - 29);

export default function HospitalReports() {
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("revenue");
  const [from, setFrom] = useState(isoDate(start30));
  const [to, setTo] = useState(isoDate(today));
  const [revGroup, setRevGroup] = useState("day");
  const [apptGroup, setApptGroup] = useState("status");
  const [revenue, setRevenue] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/hospital/reports/overview").then((r) => setOverview(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { from, to };
    Promise.all([
      api.get("/hospital/reports/revenue", { params: { ...params, group_by: revGroup } }),
      api.get("/hospital/reports/appointments", { params: { ...params, group_by: apptGroup } }),
    ]).then(([r, a]) => { setRevenue(r.data); setAppts(a.data); })
      .finally(() => setLoading(false));
  }, [from, to, revGroup, apptGroup]);

  return (
    <div className="space-y-6" data-testid="hospital-reports">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ reports</div>
          <h1 className="mt-2 text-3xl font-light tracking-tight">Reports</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportCsvButton
            endpoint="/api/hospital/exports/reports/revenue.csv"
            filename="revenue"
            label="Export revenue CSV"
            params={{ from, to, group_by: revGroup }}
            testid="hosp-rev-export"
          />
          <ExportCsvButton
            endpoint="/api/hospital/exports/reports/appointments.csv"
            filename="appointments-report"
            label="Export appts CSV"
            params={{ from, to, group_by: apptGroup }}
            testid="hosp-appts-report-export"
          />
        </div>
      </div>

      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="overview-kpis">
          <Stat label="Today" value={overview.today} testid="kpi-today" />
          <Stat label="This week" value={overview.this_week} testid="kpi-week" />
          <Stat label="Cancellations · month" value={overview.cancellations_month} testid="kpi-cancel-month" />
          <Stat label="Net rev · month" value={formatCurrency(overview.net_revenue_month, { decimals: true })} testid="kpi-net-month" />
        </div>
      )}

      {overview?.top_doctors?.length > 0 && (
        <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="top-doctors">
          <div className="px-6 py-4 border-b border-mb-border text-sm font-medium">Top doctors · this month</div>
          <ul className="divide-y divide-mb-border">
            {overview.top_doctors.map((d, i) => (
              <li key={d.doctor_id} className="px-5 py-3 flex items-center gap-3" data-testid={`top-doc-${d.doctor_id}`}>
                <div className="text-mb-fg2 w-5 text-sm">{i + 1}.</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{d.full_name}</div>
                  <div className="text-xs text-mb-fg2 truncate">{d.specialty}</div>
                </div>
                <div className="text-sm text-teal-300 tabular-nums">{d.count} appts</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="rounded-xl border border-mb-border bg-mb-surface p-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <DateField label="From" value={from} onChange={setFrom} testid="hr-from" />
        <DateField label="To" value={to} onChange={setTo} testid="hr-to" />
        <GroupField label="Revenue group" value={revGroup} onChange={setRevGroup} options={["day", "month", "doctor"]} testid="hr-rev-group" />
        <GroupField label="Appts group" value={apptGroup} onChange={setApptGroup} options={["status", "doctor", "day"]} testid="hr-appt-group" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-mb-surface border border-mb-border p-1 rounded-lg">
          <TabsTrigger value="revenue" data-testid="hr-tab-revenue" className="data-[state=active]:bg-teal-500/10 data-[state=active]:text-teal-300 rounded-md">Revenue</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="hr-tab-appointments" className="data-[state=active]:bg-teal-500/10 data-[state=active]:text-teal-300 rounded-md">Appointments</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue" className="mt-6">
          <ChartCard title={`Revenue by ${revGroup}`} testid="hr-chart-revenue">
            {loading ? "Loading…" : revenue.length === 0 ? <Empty /> : (
              <div className="h-72">
                <ResponsiveContainer>
                  {revGroup === "day" || revGroup === "month" ? (
                    <LineChart data={revenue}>
                      <CartesianGrid stroke="rgb(var(--mb-border))" />
                      <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                      <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip contentStyle={{ background: "rgb(var(--mb-surface2))", border: "1px solid rgb(var(--mb-border))", color: "rgb(var(--mb-fg))" }} formatter={(v) => formatCurrency(v, { decimals: true })} />
                      <Line type="monotone" dataKey="net" stroke={TEAL} strokeWidth={2} dot={{ fill: TEAL, r: 3 }} />
                    </LineChart>
                  ) : (
                    <BarChart data={revenue} layout="vertical" margin={{ left: 100 }}>
                      <CartesianGrid stroke="rgb(var(--mb-border))" horizontal={false} />
                      <XAxis type="number" stroke="#71717a" fontSize={11} tickFormatter={(v) => formatCurrency(v)} />
                      <YAxis dataKey="label" type="category" stroke="#71717a" fontSize={11} width={140} />
                      <Tooltip contentStyle={{ background: "rgb(var(--mb-surface2))", border: "1px solid rgb(var(--mb-border))", color: "rgb(var(--mb-fg))" }} formatter={(v) => formatCurrency(v, { decimals: true })} />
                      <Bar dataKey="net" fill={TEAL} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        </TabsContent>

        <TabsContent value="appointments" className="mt-6">
          <ChartCard title={`Appointments by ${apptGroup}`} testid="hr-chart-appointments">
            {loading ? "Loading…" : appts.length === 0 ? <Empty /> : (
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={appts}>
                    <CartesianGrid stroke="rgb(var(--mb-border))" vertical={false} />
                    <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                    <YAxis stroke="#71717a" fontSize={11} />
                    <Tooltip contentStyle={{ background: "rgb(var(--mb-surface2))", border: "1px solid rgb(var(--mb-border))", color: "rgb(var(--mb-fg))" }} />
                    <Bar dataKey="count" fill={TEAL} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-mb-border bg-mb-surface p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">{label}</div>
      <div className="mt-2 text-2xl font-light text-white">{value}</div>
    </div>
  );
}
function DateField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} className="mt-2 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
    </div>
  );
}
function GroupField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-2 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-mb-surface2 border-mb-border text-white">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
function ChartCard({ title, children, testid }) {
  return (
    <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid={testid}>
      <div className="px-6 py-4 border-b border-mb-border text-sm font-medium">{title}</div>
      <div className="p-5">{children}</div>
    </section>
  );
}
function Empty() {
  return <div className="h-72 flex items-center justify-center text-sm text-zinc-600">No data in this range.</div>;
}
